<template>
    <div class="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6 transition-all duration-200 ease-in-out">
        <div class="md:grid md:grid-cols-3 md:gap-6" v-if="!state.saved">
            <div class="md:col-span-1">
                <h3 class="text-lg font-medium leading-6 text-gray-900">Product</h3>
                <p class="mt-1 text-sm text-gray-500">What is this customer purchasing? Details can be customised to fit the sale in the following step.</p>
            </div>
            <div class="mt-5 md:mt-0 md:col-span-2">
                <form class="space-y-6" method="POST" @submit.prevent>
                    <div>
                        <Listbox as="div" v-model="state.product" v-if="products.length > 0">
                            <ListboxLabel class="block text-sm font-medium text-gray-700">Product</ListboxLabel>
                            <div class="mt-1 relative">
                                <ListboxButton class="bg-white relative w-full border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                                    <span class="block truncate">{{ state.product.name }}</span>
                                    <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                        <ChevronUpDownIcon class="h-5 w-5 text-gray-400" aria-hidden="true"/>
                                    </span>
                                </ListboxButton>

                                <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
                                    <ListboxOptions class="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                                        <ListboxOption as="template" v-for="product in products" :key="product.id" :value="product" v-slot="{ active, selected }">
                                            <li :class="[active ? 'text-white bg-indigo-600' : 'text-gray-900', 'cursor-default select-none relative py-2 pl-3 pr-9']">
                                                <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">
                                                    {{ product.name }}
                                                </span>
                                                <span v-if="selected" :class="[active ? 'text-white' : 'text-indigo-600', 'absolute inset-y-0 right-0 flex items-center pr-4']">
                                                    <CheckIcon class="h-5 w-5" aria-hidden="true"/>
                                                </span>
                                            </li>
                                        </ListboxOption>
                                    </ListboxOptions>
                                </transition>
                            </div>
                        </Listbox>

                        <Listbox as="div" v-model="state.price" v-if="prices.length > 0" class="mt-2">
                            <ListboxLabel class="block text-sm font-medium text-gray-700">Price</ListboxLabel>
                            <div class="mt-1 relative">
                                <ListboxButton class="bg-white relative w-full border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                                    <span class="block truncate">{{ state.price.product.name }}&nbsp;{{ state.price.recurring ? '(' + state.price.nickname + ')' : '' }}</span>
                                    <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                        <ChevronUpDownIcon class="h-5 w-5 text-gray-400" aria-hidden="true"/>
                                    </span>
                                </ListboxButton>

                                <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
                                    <ListboxOptions class="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                                        <ListboxOption as="template" v-for="price in prices" :key="price.id" :value="price" v-slot="{ active, selected }">
                                            <li :class="[active ? 'text-white bg-indigo-600' : 'text-gray-900', 'cursor-default select-none relative py-2 pl-3 pr-9']">
                                                <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">
                                                    {{ price.product.name }}&nbsp;{{ price.recurring ? '(' + price.nickname + ')' : '' }}
                                                </span>
                                                <span v-if="selected" :class="[active ? 'text-white' : 'text-indigo-600', 'absolute inset-y-0 right-0 flex items-center pr-4']">
                                                    <CheckIcon class="h-5 w-5" aria-hidden="true"/>
                                                </span>
                                            </li>
                                        </ListboxOption>
                                    </ListboxOptions>
                                </transition>
                            </div>
                        </Listbox>
                        <div class="mt-2" v-if="type.key !== 'subscription'">
                            <label for="quantity" class="block text-sm font-medium text-gray-700">Quantity</label>
                            <div class="mt-1">
                                <input id="quantity" name="quantity" type="number" class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" v-model="quantity" />
                            </div>
                        </div>
                    </div>
                    <div class="border-2 border-dashed border-gray-200 p-4">
                        <div v-if="Object.keys(state?.price ?? {}).length > 0">
                            <div class="font-bold text-md flex mb-2">
                                <span class="flex-none mr-2">{{ state.price.product.name }}</span>
                                <span class="shrink text-gray-400 text-xs font-normal italic truncate mt-1">( {{ state.price.id }} )</span>
                            </div>

                            <div class="mb-2 text-center">
                                <p class="font-bold text-2xl" v-if="!state.price.recurring">{{ currencyFormat(state.price.currency, state.price.unit_amount * quantity) }}</p>
                                <p class="font-bold text-2xl" v-if="state.price.recurring">{{ currencyFormat(state.price.currency, state.price.unit_amount * quantity) }} <span class="ml-2 text-sm text-gray-400 font-normal">/ {{ formatRecurring(state.price) }}</span></p>
                            </div>

                            <div class="flex mt-4" :class="[state.price.recurring ? 'justify-between' : 'justify-center']">
                                <button type="submit" @click="proceed" class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                    <ChevronDownIcon class="h-5 w-5 mr-2" />
                                    Proceed
                                </button>
                                <button type="button" v-if="state.price.recurring && !showingEnablements" @click="showEnablements" class="inline-flex items-center px-3 py-2 border shadow-sm text-sm leading-4 font-medium rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                    <ArrowPathIcon class="h-5 w-5 mr-2 animate-spin" v-if="loadingEnablements" />
                                    <EyeIcon class="h-5 w-5 mr-2" v-if="!loadingEnablements" />
                                    Show Enablements
                                </button>
                                <button type="button" v-if="state.price.recurring && showingEnablements" @click="showingEnablements = false" class="inline-flex items-center px-3 py-2 border shadow-sm text-sm leading-4 font-medium rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                    <EyeSlashIcon class="h-5 w-5 mr-2" />
                                    Hide Enablements
                                </button>
                            </div>

                            <div class="font-normal text-sm mt-4" v-if="showingEnablements">
                                <p v-if="!state.enablements"><ArrowPathIcon class="animate-spin h-5 w-5 text-gray-400" /></p>
                                <template v-if="state.enablements">
                                    <ul v-for="enablement in state.enablements">
                                        <li class="flex">
                                            <div class="mr-2 font-bold w-7 text-sm text-center">
                                                <span class="text-center" v-if="enablementProperties[enablement.permission]?.type === Boolean">
                                                    <CheckIcon class="text-green-400 h-4 w-4 mt-1 mx-auto" v-if="enablement.value === 1" />
                                                    <XMarkIcon class="text-red-400 h-4 w-4 mt-1 mx-auto" v-else />
                                                </span>
                                                <span class="text-center" v-else>
                                                    {{ enablement.value }}
                                                </span>
                                            </div>
                                            <span class="capitalize">{{ formatPermission(enablement.permission) }}</span>
                                        </li>
                                    </ul>
                                </template>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>

        <div class="md:grid md:grid-cols-3 md:gap-6" v-if="state.saved">
            <div class="md:col-span-1 flex">
                <CheckCircleIcon class="h-6 w-6 text-green-600 mr-4" />
                <h3 class="text-lg font-medium leading-6 text-gray-900 flex-1">Product</h3>
            </div>
            <div class="mt-5 md:mt-0 md:col-span-2">
                <p class="font-normal text-md">
                    {{ state.price.product.name }}&nbsp;{{ state.price.recurring ? '(' + state.price.nickname + ')' : '' }}
                    <br />
                    <span class="text-gray-400 text-xs font-normal italic">({{ state.price.id }})</span>
                </p>
                <p class="font-normal text-md" v-if="!state.price.recurring">{{ currencyFormat(state.price.currency, state.price.unit_amount * quantity) }}</p>
                <p class="font-normal text-md" v-if="state.price.recurring">{{ currencyFormat(state.price.currency, state.price.unit_amount * quantity) }} <span class="ml-2 text-sm text-gray-400 font-normal">/ {{ formatRecurring(state.price) }}</span></p>
            </div>
        </div>
    </div>
</template>

<script>
import {onMounted, ref, toRef, watch} from "vue"
import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from '@headlessui/vue'
import {CheckIcon, ChevronUpDownIcon, XMarkIcon} from '@heroicons/vue/24/solid'
import {ChevronDownIcon, ArrowPathIcon, EyeIcon, EyeSlashIcon, CheckCircleIcon} from "@heroicons/vue/24/outline"

const enablementProperties = {
    'media-requests': {
        type: Boolean,
    },
    'media-lists': {
        type: Number,
    },
    'user-accounts': {
        type: Number,
    },
    'press-releases': {
        type: Number,
    },
    'profiles': {
        type: Number,
    },
    'copywriting': {
        type: Number,
    },
    'image-attachments': {
        type: Number,
    },
    'detailed-analytics': {
        type: Boolean,
    },
    'historical-publication': {
        type: Boolean,
    },
    'media-database': {
        type: Boolean,
    },
    'custom-press-room': {
        type: Boolean,
    }
}

export default {
    name: "Product",
    props: {
        product: {
            type: Object,
            required: true,
        },
        type: {
            type: Object,
            required: true,
        }
    },
    components: {
        Listbox,
        ListboxButton,
        ListboxLabel,
        ListboxOption,
        ListboxOptions,
        CheckIcon,
        ChevronUpDownIcon,
        ArrowPathIcon,
        XMarkIcon,
        ChevronDownIcon,
        EyeIcon,
        EyeSlashIcon,
        CheckCircleIcon,
    },
    setup(props) {
        const loadingEnablements = ref(false)
        const showingEnablements = ref(false)

        const prices = ref([])
        const products = ref([])
        const state = toRef(props, 'product')
        const type = toRef(props, 'type')
        const quantity = ref(1)

        const loadProducts = async () => {
            await axios.get(route('transactions.products', {type: type.value.key}))
                .then((response) => {
                    products.value = response.data
                    state.value.product = products.value[0]
                })
        }

        const loadPricesForProduct = async () => {
            await axios.get(route('transactions.prices', {
                product: state.value.product.id
            }))
                .then((response) => {
                    prices.value = response.data.data
                    state.value.price = prices.value[0]
                })
        }

        watch(() => state.value?.product, (newValue, oldValue) => {
            loadPricesForProduct()
        })

        watch(() => type.value?.key, (newValue, oldValue) => {
            quantity.value = 1
            loadProducts()
        })

        onMounted(() => {
            loadProducts()
        })

        const formatPermission = (permission) => {
            return permission.split('-').join(' ')
        }

        const formatRecurring = (price) => {
            return (price.recurring.interval_count === 1 ? '' : price.recurring.interval_count + ' ') + (price.recurring.interval)
        }

        const showEnablements = async () => {
            loadingEnablements.value = true
            await axios.get(route('transactions.products.enablements', state.value.price.id))
                .then((response) => {
                    loadingEnablements.value = false
                    showingEnablements.value = true
                    state.value.enablements = response.data
                })
        }

        const proceed = async () => {
            state.value.quantity = quantity
            state.value.saved = true
        }

        return {
            enablementProperties,
            loadingEnablements,
            showingEnablements,
            products,
            prices,
            state,
            type,
            quantity,
            formatPermission,
            formatRecurring,
            showEnablements,
            proceed,
        }
    }
}
</script>

<style scoped>

</style>
